<template>
  <div class="modal-overflow">
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Name</label>
              <el-input type="text"
                     name="name"
                     placeholder="Name"
                     clearable
                     @blur="generateKey"
                     v-validate="modelValidations.name"
                     v-model="edit.name" />
              <small class="text-danger" v-show="errors.has('name')">
                {{ getError('name') }}
              </small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Key</label>
              <el-input type="text"
                     name="key"
                     placeholder="Key"
                     clearable
                     v-validate="modelValidations.key"
                     v-model="edit.key" />
              <small class="text-danger" v-show="errors.has('key')">
                {{ getError('key') }}
              </small>
            </div>
          </div>
        </div><!-- row -->
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Departments</label>
              <el-select class="select" multiple
                         size="large"
                         placeholder="Departments"
                         v-model="edit.departments">
                <el-option v-for="option in allDepartments"
                           class="select"
                           :value="option.value"
                           :label="option.label"
                           :key="option.label">
                </el-option>
              </el-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <button id="btn-add-field" class="btn btn-info btn-fill btn-icon btn-sm pull-right" @click="addField">
              +
            </button>
            <h6 class="task-template-heading">Fields</h6>
            <fields-table :dynamicFields="edit.fields"></fields-table>
          </div>
        </div>
        <div class="row" style="margin-top: 15px">
          <div class="col-md-12">
            <button v-if="!editMode" type="button" @click="importTaskTemplate" class="btn btn-fill btn-info btn-wd">Import</button>
            <button v-if="editMode" type="button" @click="exportTaskTemplate" class="btn btn-fill btn-info btn-wd">Export</button>
            <button type="submit" :disabled="isSubmitDisabled" @click.prevent="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right">Save</button>
            <div class="form-group form-group-active-label">
              <label class="active-label">Active</label>
              <p-switch v-model="edit.active" type="primary" on-text="YES" off-text="NO"></p-switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {notifySuccess, notifyError, clearLocalStorage} from 'src/utilities/utils'
  import {mapGetters} from 'vuex'
  import {mapFields} from 'vee-validate'
  import {OK} from 'http-status-codes'
  import TaskField from './TaskField'
  import FieldsTable from './FieldsTable'
  import Constants from 'src/utilities/constants'
  import PButton from 'src/components/UIComponents/Button.vue'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import {sync} from 'vuex-pathify'

  export default {
    props: ['data'],
    components: {
      TaskField,
      FieldsTable,
      PButton,
      PSwitch
    },
    computed: {
      isSubmitDisabled() {
        return this.isProcessing
      },
      edit: sync('tasks/templates@edit'),
      ...mapFields([
        'name',
        'key'
      ]),
      ...mapGetters({
        dynamicFields: 'tasks/templates/edit/fields',
        allDepartments: 'departments/kvpsForSelect'
      })
    },
    data() {
      return {
        heading: 'Create Task Template',
        editMode: false,
        isProcessing: false,
        modelValidations: {
          name: {
            required: true,
            max: 128
          },
          key: {
            required: true,
            max: 128
          }
        }
      }
    },
    methods: {
      generateKey() {
        this.$store.commit('tasks/templates/generateKey')
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async validateAndSubmit() {
        this.isProcessing = true

        if (await this.$validator.validateAll()) {
          await this.saveTaskTemplate()
        }
        this.isProcessing = false
      },
      async saveTaskTemplate() {
        const payload = this.$store.get('tasks/templates/savePayload', 'task-templates')
        const response = await this.$store.dispatch('save', payload)
        await this.$validator.reset()

        if (response.result === Constants.RETURN_CODES.SUCCESS) {
          this.clearCacheLocalStorage()
          this.$eventHub.$emit('taskTemplateSaved')
          await this.$store.dispatch('tasks/templates/fetchAll')
          this.$emit('close')
          notifySuccess(this, 'Task Template Saved Successfully')
        } else {
          notifyError(this, 'Failed to Save Task Template', response.errors)
        }
      },
      addField() {
        this.$eventHub.$emit('show-modal-2', 'Task Field', null, '500px')
      },
      disableEditMode() {
        this.$store.commit('tasks/templates/resetEdit')
        this.editMode = false
      },
      enableEditMode() {
        this.editMode = true
      },
      hasData() {
        return this.data && this.data.id
      },
      clearCacheLocalStorage() {
        // purge the task names cache after a task name has been added or edited
        if (this.$store.cache.has('tasks/templates/fetchAll')) {
          this.$store.cache.delete('tasks/templates/fetchAll')
        }
        clearLocalStorage(Constants.LOCAL_STORAGE.LOCATION.TASK_TEMPLATES)
      },
      importTaskTemplate() {
        this.$eventHub.$emit('show-modal-1', 'Import Task Template', null, '700px')
      },
      exportTaskTemplate() {
        this.$eventHub.$emit('show-modal-2', 'Export Task Template', this.data, '600px')
      }
    },
    async beforeMount() {
      if (this.hasData()) {
        await this.$store.dispatch('tasks/templates/fetchFieldsInUse', this.data.id)
        const response = await this.$store.dispatch('fetchById', {
          endpoint: 'task-templates',
          id: this.data.id
        })

        if (OK === response.status) {
          this.$store.commit('tasks/templates/hydrateEdit', response.data)
        }

        this.enableEditMode()
      }
    },
    mounted() {
      this.$eventHub.$on('taskTemplateReset', this.disableEditMode)
    },
    destroyed() {
      this.$store.commit('tasks/templates/resetEdit')
    }
  }
</script>

<style scoped>
  label {
    display: block;
  }
  .task-template-heading {
    padding: 15px 0 0 15px;
  }
  #btn-add-field {
    margin: 5px 15px 0 0;
  }
  .card {
    box-shadow: none;
  }
</style>
